import React, { useEffect, useMemo, useState } from "react";
import { Mail, X, AlertCircle, CheckCircle2, Send } from "lucide-react";

// Modal that lets an admin preview unpaid buyers, pick which drawing to
// reference in the reminder, and dispatch the blast. Emails are sent via a
// backend BackgroundTask so this call returns immediately with a queued count.
export const PaymentReminderModal = ({
  drawings,
  fetchEligible,
  onSend,
  onClose,
  showToast,
}) => {
  const [loading, setLoading] = useState(true);
  const [eligible, setEligible] = useState({ count: 0, tickets: [] });
  const [selectedDrawingId, setSelectedDrawingId] = useState("");
  const [sending, setSending] = useState(false);
  const [sentResult, setSentResult] = useState(null); // { queued, message }

  // Load recipients + auto-pick the nearest upcoming drawing so the admin
  // doesn't have to think about defaults.
  useEffect(() => {
    let live = true;
    (async () => {
      const res = await fetchEligible();
      if (!live) return;
      if (res.ok) {
        setEligible({ count: res.count, tickets: res.tickets || [] });
      } else {
        showToast?.(res.error || "Could not load recipients");
      }
      setLoading(false);
    })();
    return () => { live = false; };
  }, [fetchEligible, showToast]);

  // Pick the nearest upcoming drawing by default.
  useEffect(() => {
    if (selectedDrawingId || !drawings || drawings.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = [...drawings]
      .filter((d) => d.drawing_date >= today)
      .sort((a, b) => a.drawing_date.localeCompare(b.drawing_date))[0];
    setSelectedDrawingId((upcoming || drawings[drawings.length - 1]).id);
  }, [drawings, selectedDrawingId]);

  const selectedDrawing = useMemo(
    () => drawings.find((d) => d.id === selectedDrawingId),
    [drawings, selectedDrawingId]
  );

  const doSend = async () => {
    if (!selectedDrawingId) return;
    setSending(true);
    const res = await onSend(selectedDrawingId);
    setSending(false);
    if (res.ok) {
      setSentResult({ queued: res.queued, message: res.message });
    } else {
      showToast?.(res.error || "Could not send reminders");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(11,18,20,0.55)" }}
      onClick={onClose}
      data-testid="payment-reminder-modal"
    >
      <div
        className="rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4" style={{ borderBottom: "1px solid var(--border-default)" }}>
          <div>
            <h3 className="text-base font-bold flex items-center gap-2" style={{ color: "var(--ink-primary)" }}>
              <Mail className="h-4 w-4" style={{ color: "var(--brand-primary)" }} />
              Payment reminder blast
            </h3>
            <p className="mt-0.5 text-xs" style={{ color: "var(--ink-tertiary)" }}>
              Sends a friendly email to every buyer whose ticket is sold-but-not-paid and has an email on file.
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="reminder-close" className="p-1 rounded" aria-label="Close">
            <X className="h-5 w-5" style={{ color: "var(--ink-tertiary)" }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {sentResult ? (
            <div className="rounded-lg border p-4 flex items-start gap-3" style={{ background: "var(--semantic-success-soft)", borderColor: "var(--semantic-success)" }} data-testid="reminder-success">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "var(--semantic-success)" }} />
              <div>
                <div className="font-semibold" style={{ color: "var(--ink-primary)" }}>
                  Queued {sentResult.queued} reminder{sentResult.queued === 1 ? "" : "s"}.
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--ink-secondary)" }}>
                  Emails are dispatching in the background — this typically finishes within a minute. Check the Activity Log for per-ticket confirmations.
                </div>
              </div>
            </div>
          ) : loading ? (
            <div className="text-sm py-8 text-center" style={{ color: "var(--ink-tertiary)" }} data-testid="reminder-loading">
              Loading recipients…
            </div>
          ) : eligible.count === 0 ? (
            <div className="rounded-lg border p-4 flex items-start gap-3" style={{ background: "var(--semantic-info-soft)", borderColor: "var(--border-default)" }} data-testid="reminder-empty">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "var(--semantic-info)" }} />
              <div>
                <div className="font-semibold" style={{ color: "var(--ink-primary)" }}>No reminders to send</div>
                <div className="text-xs mt-1" style={{ color: "var(--ink-secondary)" }}>
                  Either every sold ticket has been paid, or the sold-but-not-paid ones don&rsquo;t have a buyer email on file. Add emails via the ticket modal to include those buyers.
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Drawing picker */}
              <div>
                <label htmlFor="reminder-drawing" className="text-xs font-semibold uppercase tracking-wide block mb-1" style={{ color: "var(--ink-tertiary)" }}>
                  Drawing to reference in the email
                </label>
                <select
                  id="reminder-drawing"
                  data-testid="reminder-drawing-select"
                  value={selectedDrawingId}
                  onChange={(e) => setSelectedDrawingId(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: "var(--border-default)", background: "var(--surface-card)", color: "var(--ink-primary)" }}
                >
                  <option value="" disabled>Pick a drawing…</option>
                  {drawings.map((d) => (
                    <option key={d.id} value={d.id} style={{ background: "var(--surface-card)", color: "var(--ink-primary)" }}>
                      {d.drawing_date} — {d.name} {d.drawing_type === "main" ? "(Main)" : "(Early Bird)"}
                    </option>
                  ))}
                </select>
                {selectedDrawing && (
                  <p className="text-xs mt-1" style={{ color: "var(--ink-tertiary)" }}>
                    The email will ask buyers to pay before <strong style={{ color: "var(--ink-secondary)" }}>{selectedDrawing.drawing_date}</strong>.
                  </p>
                )}
              </div>

              {/* Recipient preview */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-tertiary)" }}>
                    Recipients ({eligible.count})
                  </div>
                </div>
                <div className="border rounded-md max-h-72 overflow-y-auto" style={{ borderColor: "var(--border-default)" }} data-testid="reminder-recipient-list">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0" style={{ background: "var(--surface-subtle)" }}>
                      <tr style={{ color: "var(--ink-tertiary)" }}>
                        <th className="px-3 py-2 text-left font-medium">Ticket</th>
                        <th className="px-3 py-2 text-left font-medium">Buyer</th>
                        <th className="px-3 py-2 text-left font-medium">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eligible.tickets.map((t) => (
                        <tr key={t.number} className="border-t" style={{ borderColor: "var(--border-default)" }}>
                          <td className="px-3 py-1.5 font-semibold" style={{ color: "var(--brand-primary)" }}>#{t.number}</td>
                          <td className="px-3 py-1.5" style={{ color: "var(--ink-primary)" }}>{t.buyer || "—"}</td>
                          <td className="px-3 py-1.5 font-mono text-xs" style={{ color: "var(--ink-secondary)" }}>{t.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex items-start gap-2 text-xs rounded-md p-2.5" style={{ background: "var(--semantic-warning-soft)", color: "var(--ink-secondary)" }}>
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: "var(--semantic-warning)" }} />
                <span>
                  This sends <strong>one email per recipient</strong>. Reminders are queued to a background job so this dialog closes fast even for large lists.
                </span>
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 px-5 py-3" style={{ borderTop: "1px solid var(--border-default)", background: "var(--surface-subtle)" }}>
          {sentResult ? (
            <button
              type="button"
              onClick={onClose}
              data-testid="reminder-done"
              className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-md text-sm font-semibold text-white transition-colors hover:opacity-90"
              style={{ background: "var(--brand-primary)" }}
            >
              Done
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={doSend}
                disabled={sending || loading || eligible.count === 0 || !selectedDrawingId}
                data-testid="reminder-send-btn"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: "var(--brand-primary)" }}
              >
                <Send className="h-4 w-4" />
                {sending ? "Sending…" : `Send to ${eligible.count} buyer${eligible.count === 1 ? "" : "s"}`}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 rounded-md text-sm font-semibold border transition-colors hover:bg-[color:var(--surface-card)]"
                style={{ borderColor: "var(--border-default)", color: "var(--ink-secondary)", background: "var(--surface-card)" }}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
